import timeStampToDate from '@/helpers/timeStampToDate';
import { dateFilterGetter } from '../../common/components/ag-grid/configs';
import NumberRenderer from '../../common/components/CellRenderers/NumberRenderer';
import { Button, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useInvoiceIssuanceMutation } from '@/app/Purchase/api/ordersApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { warningMessage, successMessage } from '@/utils/toast';
import { useDispatch } from 'react-redux';
import SubtleBadge from '../../../components/common/SubtleBadge';
import {
  OrderStatusRenderer,
  OrderFactorRenderer
} from '../../common/components/CellRenderers/OrderStatusRenderer';
import {
  breakpointsMediaQuery,
  useMediaQuery,
  toPersianDigit
} from '@/helpers/utils';
import { MdCheck, MdContentCopy } from 'react-icons/md';
import { useState } from 'react';
import dayjs from 'dayjs';
import type { ICellRendererParams } from 'ag-grid-community';
import type { OrderListItem } from '../types/ordersApiTypes';
import {
  formatOrderTariffLabel,
  getNormalizedTariffName,
  getOrderAddonDisplayLabels,
} from '../utils/orderAddonDisplay';

const menuTabs = ['columnsMenuTab', 'filterMenuTab', 'generalMenuTab'];

function OrderTariffNameCell({ data }: { data?: OrderListItem }) {
  const tariffLabel = getNormalizedTariffName(data?.tariff_name);
  const addonLabels = getOrderAddonDisplayLabels(data?.addon_names);

  return (
    <>
      {tariffLabel}
      {addonLabels.length > 0 ? (
        <span className="fs--2 text-700">
          {' '}
          ({addonLabels.join('، ')})
        </span>
      ) : null}
    </>
  );
}

function OrderRowActions({
  orderId,
  onViewOrderDetails,
}: {
  orderId?: string;
  onViewOrderDetails: (orderId: string) => void;
}) {
  if (!orderId) return null;

  return (
    <span className="allaho_akbar_zIndex">
      <Dropdown
        onClick={(event) => event.stopPropagation()}
        align="end"
        className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
      >
        <Dropdown.Toggle
          className="text-600 btn-reveal"
          split
          variant="reveal"
          size="sm"
        >
          <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
        </Dropdown.Toggle>
        <Dropdown.Menu className="border py-0">
          <Dropdown.Item
            onClick={() => onViewOrderDetails(orderId)}
            className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
          >
            <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
            جزئیات
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </span>
  );
}

export const ordersColumnConfig = (
  setShowLegalModal: (show: boolean) => void,
  isMdUp: boolean,
  doesTaxIdExist: boolean,
  factor_status: number | null | undefined,
  real_and_legal_updated_at: string | number | null | undefined,
  onViewOrderDetails: (orderId: string) => void,
) => {
  const baseColumns = [
    {
      field: 'tariff_name',
      type: 'string',
      title: 'جزئیات خرید',
      width: 280,
      searchable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      menuTabs,
      valueGetter: (params: { data?: OrderListItem }) =>
        formatOrderTariffLabel(params.data?.tariff_name, params.data?.addon_names),
      cellRenderer: (params: ICellRendererParams<OrderListItem>) => {
        const orderId = params.data?.id;

        return (
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span
              className="d-flex align-items-center gap-2 cursor-pointer"
              title="مشاهده جزئیات"
              role="button"
              tabIndex={0}
              onClick={() => {
                if (orderId) onViewOrderDetails(orderId);
              }}
              onKeyDown={(event) => {
                if (
                  orderId &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault();
                  onViewOrderDetails(orderId);
                }
              }}
            >
              <FontAwesomeIcon
                icon="circle"
                className={
                  params.data?.status === 0
                    ? 'text-success'
                    : params.data?.status === 2
                      ? 'text-danger'
                      : params.data?.status === 1 ||
                          params.data?.status === 3 ||
                          params.data?.status === 4
                        ? 'text-warning'
                        : 'd-none'
                }
                style={{ fontSize: 9 }}
              />
              <OrderTariffNameCell data={params.data} />
            </span>
            <SubtleBadge bg="info">{params.data?.period}</SubtleBadge>
          </div>
        );
      }
    },
    {
      field: 'amount_paid',
      type: 'number',
      title: 'مبلغ پرداختی (تومان)',
      minWidth: 170,
      flex: 1,
      searchable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      menuTabs,
      cellRenderer: (params: ICellRendererParams<OrderListItem>) => (
        <div className="d-flex justify-content-center align-items-center">
          {params.value === 0 ? (
            'رایگان'
          ) : (
            <NumberRenderer text={params.value} />
          )}
        </div>
      )
    },

    // جایگاه ستون شماره مالیاتی
    doesTaxIdExist
      ? {
          field: 'tax_id',
          type: 'string',
          title: 'شماره مالیاتی',
          width: 200,
          searchable: true,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          menuTabs,
          cellRenderer: (params: ICellRendererParams<OrderListItem>) => {
            const [copied, setCopied] = useState(false);

            return (
              params.value && (
                <div className="d-flex justify-content-center align-items-center">
                  {copied ? (
                    <MdCheck size={18} />
                  ) : (
                    <MdContentCopy
                      title="کپی شماره مالیاتی"
                      onClick={e => {
                        navigator.clipboard.writeText(params.value).then(() => {
                          setCopied(true);
                          successMessage('شماره مالیاتی کپی شد.');
                          setTimeout(() => {
                            setCopied(false);
                          }, 3000);
                        });
                      }}
                      className="cursor-pointer little-icon"
                      style={{ paddingLeft: '3px' }}
                      size={18}
                    />
                  )}
                  {params.value}
                </div>
              )
            );
          }
        }
      : null,

    {
      field: 'ordered_at',
      type: 'string',
      title: 'تاریخ سفارش',
      menuTabs,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      filterValueGetter: dateFilterGetter,
      width: 160,
      searchable: true,
      valueFormatter: (params: { value?: string | number }) =>
        toPersianDigit(timeStampToDate(Number(params.value)).slice(8)),
    },

    {
      field: 'expired_at',
      type: 'string',
      title: 'تاریخ انقضا',
      menuTabs,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      filterValueGetter: dateFilterGetter,
      width: 140,
      searchable: true,
      valueFormatter: (params: { value?: string | number }) =>
        toPersianDigit(timeStampToDate(Number(params.value)).slice(8)),
    },

    {
      field: 'status',
      type: 'string',
      title: 'وضعیت ',
      menuTabs,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      width: 135,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<OrderListItem>) => (
        <OrderStatusRenderer status={params.value} />
      ),
    },

    {
      field: 'invoice_issued',
      type: 'number',
      title: 'صدور فاکتور',
      width: 180,
      pinned: isMdUp && 'left',
      searchable: false,
      sortable: false,
      filter: 'agTextColumnFilter',
      floatingFilter: false,
      menuTabs,
      cellRenderer: (params: ICellRendererParams<OrderListItem>) => {
        const dispatch = useDispatch();
        const [invoiceIssuance] = useInvoiceIssuanceMutation();
        const invoiceArgs = {
          order_id: params?.data?.id
        };
        //  ----------------------
        const orderedAt = dayjs(
          timeStampToDate(Number(params.data.ordered_at)).slice(8)
        ).startOf('day');
        const realAndLegalUpdatedAt = dayjs(
          timeStampToDate(Number(real_and_legal_updated_at)).slice(8)
        ).startOf('day');
        const diffDays = realAndLegalUpdatedAt.diff(orderedAt, 'day');
        const noShow =
          params.data.tax_amount !== null &&
          params.data.invoice_issued === false &&
          diffDays > 30;

        //  ----------------------
        return params.data.status === 0 &&
          params.data.amount_paid !== 0 &&
          params.data.tax_amount ? (
          params.data.added_real_and_legal || params.value ? (
            <OrderFactorRenderer
              noShow={noShow}
              factor_status={factor_status}
              status={params.value}
            />
          ) : (
            <div className="d-flex justify-content-center h-100 align-items-center">
              <Button
                onClick={() => {
                  invoiceIssuance(invoiceArgs)
                    .unwrap()
                    .then(response => {
                      if (response.code === 60002) {
                        warningMessage('لطفا اطلاعات فاکتور را وارد نمایید.');
                        setShowLegalModal(true);
                      } else if (response.code === 200) {
                        dispatch;
                        window.open(`/orders/factor/${params.data.id}/`);
                      }
                    });
                }}
                variant="falcon-primary"
                size="sm"
                className="fs--2 d-flex align-items-center gap-1"
              >
                <FontAwesomeIcon icon="share-square" />
                صدور فاکتور {params.value}
              </Button>
            </div>
          )
        ) : (
          <></>
        );
      }
    },
    {
      field: 'actions',
      type: 'string',
      title: ' ',
      width: 70,
      pinned: isMdUp ? 'left' : undefined,
      searchable: false,
      sortable: false,
      filter: false,
      floatingFilter: false,
      noFilter: true,
      menuTabs,
      cellStyle: { textAlign: 'center', margin: '0px' },
      cellClass: 'over-flow',
      cellRenderer: (params: ICellRendererParams<OrderListItem>) => (
        <OrderRowActions
          orderId={params.data?.id}
          onViewOrderDetails={onViewOrderDetails}
        />
      ),
    },
  ];

  // حذف فیلد null که ممکن است در آرایه باشد (ستون مالیاتی در صورت عدم وجود)
  return baseColumns.filter(Boolean);
};
